'use client';

/**
 * Chat capabilities modal – opened from the sidebar "What can it do?" button.
 * Filter by use case (Operations, Sales, Marketing, General) and entity
 * (Contact, Company, Employee, …), or search across every field.
 *
 * This file is the container: state, wiring, and the modal shell. The pieces
 * live in `./chat-capabilities/`:
 *  - `labels.ts`               — display labels + canonical chip ordering
 *  - `filters.ts`              — pure chip derivation and filtering
 *  - `CapabilityFilterBar.tsx` — search field + the two chip axes
 *  - `CapabilityCard.tsx`      — one tile in the grid
 */

import React, { useMemo, useState } from 'react';
import { ResponsiveModal } from '@wtc/ui/components/responsive-modal';
import { useGlobalModalStack } from '@wtc/ui/components/global-modal-stack';
import { Plug } from 'lucide-react';
import { ALL_KEY } from './chat-capabilities/labels';
import {
  availableEntities,
  availableUseCaseGroups,
  filterCapabilities,
} from './chat-capabilities/filters';
import { CapabilityFilterBar } from './chat-capabilities/CapabilityFilterBar';
import { CapabilityCard } from './chat-capabilities/CapabilityCard';
import type { ChatToolCapabilityItem } from './chat-capabilities/types';

const CHAT_CAPABILITIES_MODAL_ID = 'chat-capabilities';

export type { ChatToolCapabilityItem };

export interface ChatCapabilitiesModalProps {
  /** List of tool capabilities. From hub: getChatToolCapabilitiesList(). */
  toolCapabilities: ChatToolCapabilityItem[];
  /** When set, shows a footer link to manage external MCP server connections (admin-only in Hub). */
  mcpConnectionsHref?: string;
}

export function ChatCapabilitiesModal({
  toolCapabilities,
  mcpConnectionsHref,
}: ChatCapabilitiesModalProps) {
  const { isModalOpen, closeModal } = useGlobalModalStack();
  const open = isModalOpen(CHAT_CAPABILITIES_MODAL_ID);
  const [selectedUseCase, setSelectedUseCase] = useState<string>(ALL_KEY);
  const [selectedEntity, setSelectedEntity] = useState<string>(ALL_KEY);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const useCaseGroups = useMemo(
    () => availableUseCaseGroups(toolCapabilities),
    [toolCapabilities]
  );
  const entities = useMemo(() => availableEntities(toolCapabilities), [toolCapabilities]);

  const filteredTools = useMemo(
    () =>
      filterCapabilities(toolCapabilities, {
        useCase: selectedUseCase,
        entity: selectedEntity,
        search: searchQuery,
      }),
    [toolCapabilities, selectedUseCase, selectedEntity, searchQuery]
  );

  return (
    <ResponsiveModal
      globalModalId={CHAT_CAPABILITIES_MODAL_ID}
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) closeModal(CHAT_CAPABILITIES_MODAL_ID);
      }}
      title="What Meridian can do"
    >
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Meridian can use these capabilities to help with operations. Ask in plain language; it
          will choose the right tools. For creates and edits, you&apos;ll be asked to confirm
          before anything is saved.
        </p>

        <CapabilityFilterBar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          useCaseGroups={useCaseGroups}
          selectedUseCase={selectedUseCase}
          onSelectUseCase={setSelectedUseCase}
          entities={entities}
          selectedEntity={selectedEntity}
          onSelectEntity={setSelectedEntity}
        />

        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          {filteredTools.length > 0 ? (
            filteredTools.map((tool) => <CapabilityCard key={tool.name} tool={tool} />)
          ) : (
            <div className="col-span-full flex items-center justify-center py-8 text-center">
              {/* The chips alone can exclude everything, with no search term to
                  quote — say so rather than rendering `matching “”`. */}
              <p className="text-sm text-muted-foreground">
                {searchQuery.trim() ? (
                  <>
                    No capabilities found matching &ldquo;
                    <span className="font-medium">{searchQuery}</span>&rdquo;
                  </>
                ) : (
                  'No capabilities match these filters.'
                )}
              </p>
            </div>
          )}
        </ul>

        {mcpConnectionsHref && (
          <div className="border-t border-border pt-3">
            <a
              href={mcpConnectionsHref}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
            >
              <Plug className="h-4 w-4" />
              Connect external MCP servers
            </a>
            <p className="mt-1 text-xs text-muted-foreground">
              Add Model Context Protocol servers to give Meridian more tools. Admin only.
            </p>
          </div>
        )}
      </div>
    </ResponsiveModal>
  );
}
